import React from 'react';
import { DatePicker } from 'antd';
import { CalendarOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useResponsive } from '../../hooks/useResponsive';

interface MobileDatePickerProps {
  value?: Dayjs | null;
  onChange?: (date: Dayjs | null) => void;
  placeholder?: string;
  format?: string;
  style?: React.CSSProperties;
  size?: 'small' | 'middle' | 'large';
  allowClear?: boolean;
  disabled?: boolean;
}

// Serialize Dayjs → yyyy-MM-dd via calendar components (avoids dayjs.format()
// lint restriction; we want the literal calendar date the user picked, not a
// timezone-shifted version).
const toInputValue = (d: Dayjs | null | undefined): string =>
  d ? `${d.year()}-${String(d.month() + 1).padStart(2, '0')}-${String(d.date()).padStart(2, '0')}` : '';

const toDisplayValue = (d: Dayjs | null | undefined): string =>
  d ? `${String(d.date()).padStart(2, '0')}/${String(d.month() + 1).padStart(2, '0')}/${d.year()}` : '';

/**
 * Overlay del input nativo: en iOS Safari un <input type="date"> vacío no pinta
 * placeholder y, con appearance:none, colapsa a un tap target de ~0px — no se
 * podía abrir el picker hasta que tuviera valor. El fix: el texto visible
 * (placeholder o fecha) es un <span> nuestro, y el input nativo se estira
 * invisible sobre toda el área, así el tap siempre cae en él y abre la rueda
 * nativa de iOS. fontSize 16 en el input evita el auto-zoom de Safari al focus.
 */
const NativeDateOverlay: React.FC<{
  inputValue: string;
  displayValue: string;
  placeholder: string;
  disabled?: boolean;
  fontSize: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ inputValue, displayValue, placeholder, disabled, fontSize, onChange }) => (
  <div style={{ position: 'relative', flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>
    <span
      style={{
        fontSize,
        color: displayValue ? 'var(--text-primary)' : 'var(--text-secondary)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {displayValue || placeholder}
    </span>
    <input
      type="date"
      value={inputValue}
      onChange={onChange}
      disabled={disabled}
      aria-label={placeholder}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        border: 'none',
        padding: 0,
        margin: 0,
        fontSize: 16,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    />
  </div>
);

/**
 * Single-date picker that uses a native HTML <input type="date"> on mobile
 * for proper iOS UX, and falls back to Ant Design DatePicker on desktop.
 * Mirrors the v1 MobileDateRangePicker convention used across the app.
 */
const MobileDatePicker: React.FC<MobileDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Fecha',
  format = 'DD/MM/YYYY',
  style,
  size = 'middle',
  allowClear = true,
  disabled = false,
}) => {
  const { isMobile } = useResponsive();

  if (isMobile) {
    const inputValue = toInputValue(value);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value ? dayjs(e.target.value) : null;
      onChange?.(next);
    };

    const inputHeight = size === 'small' ? 24 : size === 'large' ? 40 : 32;
    const fontSize = size === 'small' ? 12 : 14;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid var(--border-color)',
          borderRadius: 6,
          background: disabled ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
          padding: '0 11px',
          height: inputHeight,
          width: '100%',
          ...style,
        }}
      >
        <CalendarOutlined
          style={{ color: 'var(--text-secondary)', marginRight: 8, flexShrink: 0, fontSize: 14 }}
        />
        <NativeDateOverlay
          inputValue={inputValue}
          displayValue={toDisplayValue(value)}
          placeholder={placeholder}
          disabled={disabled}
          fontSize={fontSize}
          onChange={handleChange}
        />
        {allowClear && inputValue && !disabled && (
          <CloseCircleOutlined
            onClick={() => onChange?.(null)}
            style={{
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              marginLeft: 8,
              flexShrink: 0,
              fontSize: 12,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <DatePicker
      value={value}
      onChange={(d) => onChange?.(d)}
      placeholder={placeholder}
      format={format}
      style={{ width: '100%', ...style }}
      size={size}
      allowClear={allowClear}
      disabled={disabled}
    />
  );
};

export { NativeDateOverlay };
export default MobileDatePicker;
